import angular, { IPromise } from 'angular'
import flatten from 'lodash/flatten'
import moment from 'moment'
import { VdTracking, VdPerformanceTracking } from '@vend/vend.js'

import { trackError } from '@vend/utilities'
import { SaleTransformer } from '../../factories/sale-transformer'
import {
  SaleSyncStatus,
  UNSYNCED_SYNC_STATUSES,
} from '../../models/sale.constants'
import { LineItem } from '../../models/line-item'
import { SaleJson, Sale } from '../../models/sale'
import { $q } from '../../ng-compat/imports'
import { ReduxSaleStorageAdapter } from '../../state/slices/currentSale/reduxSaleStorageAdapter'
import { store, Store } from './database'
import { Cursor } from './database-types'

// Maximum number of uploaded sales to be removed in one batch action
const MAX_UPLOADED_SALES_TO_REMOVE = 100
const DAYS_TO_STORE_UPLOADED_SALES = 7
const REMOVE_UPLOADED_SALES_MEASURE = 'REMOVE_UPLOADED_SALES'

/**
 * Stores sales in IndexedDB.
 */
export class SalesStore {
  private salesDbStore: Store<'sales'>

  constructor(
    private readonly saleTransformer: SaleTransformer,
    private readonly vdTracking: VdTracking,
    private readonly vdPerformanceTracking: VdPerformanceTracking
  ) {
    'ngInject'
    this.salesDbStore = store('sales')
  }

  /**
   * Get the list of unsynced sales.
   *
   * @return The hydrated sales from the indexedDB store
   */
  public getUnsyncedSales() {
    return $q
      .all(
        UNSYNCED_SYNC_STATUSES.map(unsyncedStatus =>
          this.getSalesBySyncStatus(unsyncedStatus)
        )
      )
      .then(salesByStatus => flatten(salesByStatus))
  }

  /**
   * Get all sales in the sales store that have the given sync status.
   *
   * @param  syncStatus
   *         The sync status (e.g. MODIFIED, UPLOADED, ERROR, etc) to search for sales by
   *
   * @return A promise resolved with the list of hydrated sales with the given sync status
   */
  public getSalesBySyncStatus(syncStatus: SaleSyncStatus): IPromise<Sale[]> {
    return $q
      .resolve(this.salesDbStore.index('syncStatus').getAll(syncStatus))
      .then(unsyncedSalesJson =>
        $q.all(
          unsyncedSalesJson.map(sale => this.saleTransformer.toModel(sale))
        )
      )
  }

  /**
   * Get the non hydrated collection of errored sales.
   *
   * @return The non hydrated collection all errored sales.
   */
  public getErroredSalesJson(): IPromise<Array<Partial<SaleJson>>> {
    function removeSensitiveData(sale: Partial<SaleJson>) {
      delete sale.receiptEmailAddress
      delete sale.note

      if (sale.lineItems) {
        sale.lineItems.forEach((lineItem: Partial<LineItem>) => {
          delete lineItem.note
        })
      }

      return sale
    }

    const syncStatusIndex = this.salesDbStore.index('syncStatus')
    return syncStatusIndex
      .getAll(SaleSyncStatus.ERROR)
      .then(sales => flatten(sales))
      .then(erroredSales => erroredSales.map(removeSensitiveData))
  }

  /**
   * Save the given sale to the IndexedDB store.
   *
   * @param  sale
   *         The sale to save to the database
   *
   * @return A promise resolved once the sale has been saved to the database
   */
  public saveSale(sale: Sale | ReduxSaleStorageAdapter): Promise<string> {
    this.checkSaleModelLineItems(sale)
    return this.writeSaleJson(sale.toJSON())
  }

  /**
   * Write the sale to the IndexedDB store.
   *
   * @param  saleJson
   *         The JSON representation of the sale
   *
   * @return A promise resolved once the sale has been written to the database
   */
  public writeSaleJson(saleJson: SaleJson): Promise<string> {
    return this.salesDbStore.put(saleJson)
  }

  /**
   * Load the sale from the IndexedDB and transform it for use with Web Register
   *
   * @param  saleId
   *         The ID or local ID of the sale to load
   *
   * @return A promise resolved with the transformed sale loaded from the store
   */
  public loadSale(saleId: string): IPromise<Sale | undefined> {
    return this.loadSaleJson(saleId).then(
      sale => sale && this.saleTransformer.toModel(sale)
    )
  }

  /**
   * Load the sale JSON with the given local id from the IndexedDB store.
   *
   * @param  saleId
   *         The ID or local ID of the sale to load
   *
   * @return A promise resolved with the JSON representation of a sale loaded from the store
   */
  public loadSaleJson(saleId: string): IPromise<SaleJson | undefined> {
    return $q.resolve(this.salesDbStore.get(saleId)).then(sale => {
      // If we don't find a sale by local ID, attempt to find by ID
      return sale || $q.resolve(this.salesDbStore.index('byId').get(saleId))
    })
  }

  /**
   * Remove the sale with the given local ID from the indexedDB store. Primarily used to remove a sale created
   * when the register is in Training Mode
   *
   * @param  saleId The local ID of the sale to remove
   *
   * @return A promise resolved when the sale is removed from the store
   */
  public removeSale(saleId: string) {
    return $q.resolve(this.salesDbStore.del(saleId))
  }

  /**
   * Get's the total number of entries for the store.
   *
   * @return The total number of entries in the store.
   */
  public count() {
    return $q.resolve(this.salesDbStore.count())
  }

  /**
   * Batch deletes successfully uploaded sales which are more than one week old.
   *
   * @return Resolved once the uploaded sales have been removed.
   */
  public removeUploadedSales() {
    this.vdPerformanceTracking.start(REMOVE_UPLOADED_SALES_MEASURE)

    return this.getUploadedSaleIdsReadyForRemoval().then(
      uploadedSaleIdsToRemove => {
        if (!uploadedSaleIdsToRemove) {
          return
        }

        const batchSize = Object.keys(uploadedSaleIdsToRemove).length

        return $q
          .resolve(this.salesDbStore.batch(uploadedSaleIdsToRemove))
          .then(() => {
            const performanceMeasure = this.vdPerformanceTracking.stop(
              REMOVE_UPLOADED_SALES_MEASURE
            ) as PerformanceMeasure | undefined
            const duration = performanceMeasure && performanceMeasure.duration

            this.vdTracking.trackUserEvent({
              eventName: 'Batch Removal of Uploaded Sales',
              metadata: {
                batchSize,
                duration,
              },
            })
          })
          .catch(error => {
            this.vdPerformanceTracking.stop(REMOVE_UPLOADED_SALES_MEASURE)

            trackError('[Sales Store] Failed to batch delete uploaded sales', {
              data: {
                batchSize,
                error,
              },
            })
          })
      }
    )
  }

  /**
   * Retrieves all uploaded sales which are more than one week old. Returns an object of key value
   * pairs (with null values) which can be used for batch deletions.
   *
   * @return Either undefined if there are no matching sales, or an object of key values
   *         pairs (with null values).
   */
  private getUploadedSaleIdsReadyForRemoval() {
    return this.getNextBatchOfRemovableSaleIds()
      .then(uploadedSaleIds => {
        const uploadedSaleIdsToRemove: {
          [key: string]: null
        } = {}
        uploadedSaleIds.forEach(localId => {
          // Constructing an object of key values pairs (with null values) allows us to batch delete the
          // sales from indexeddb
          uploadedSaleIdsToRemove[localId] = null
        })

        if (Object.keys(uploadedSaleIdsToRemove).length) {
          return uploadedSaleIdsToRemove
        }
      })
      .catch(error => {
        trackError(
          '[Sales Store] Failed to get uploaded sales ready for removal',
          {
            data: {
              error,
            },
          }
        )
      })
  }

  /**
   * Retrieves the next 100 sale ids with an "UPLOADED" sync status, greater than one week old which can be removed.
   *
   * @return Resolved once the uploaded sale ids have been retrieved.
   */
  private getNextBatchOfRemovableSaleIds() {
    const sales: string[] = []

    const iterator = (cursor: Cursor<'sales'>) =>
      this.removableSalesIterator(sales, cursor)

    return this.salesDbStore
      .index('syncStatus')
      .cursor({ range: SaleSyncStatus.UPLOADED, iterator })
      .then(() => sales)
  }

  /**
   * Iterator used for obtaining the next batch of uploaded sales which can be removed.
   *
   * @param sales
   *        The current collection of sales.
   *
   * @param cursor
   *        The current cursor.
   */
  private removableSalesIterator(sales: string[], cursor: Cursor<'sales'>) {
    if (sales.length >= MAX_UPLOADED_SALES_TO_REMOVE) {
      return false
    }

    const sale = cursor.value
    if (sale) {
      const daysSinceSale = moment().diff(moment(sale.saleDate!), 'days')

      if (daysSinceSale > DAYS_TO_STORE_UPLOADED_SALES || !sale.saleDate) {
        sales.push(sale.localId)
      }
    }

    return true
  }

  /**
   * Checks for missing product ids on each line item in the sale.
   *
   * @param lineItems
   *        Collection of line items to evaluate
   */
  private checkSaleModelLineItems(sale: Sale | ReduxSaleStorageAdapter) {
    const { lineItems, saleDate, syncStatus, status } = sale

    if (!lineItems || !lineItems.length) {
      return
    }

    const hasMissingProducts = lineItems.some(lineItem => {
      return !lineItem.product || !lineItem.product.id
    })

    if (hasMissingProducts) {
      trackError('[Sale Store] lineItem missing product id', {
        data: {
          stack: new Error().stack,
          saleDate,
          syncStatus,
          status,
        },
      })
    }
  }
}

angular.module('webregister.factories').service('salesStore', SalesStore)
